import {
  getBinatedLifecycleByDocument,
  refreshBinatedLifecycleByDocument,
  requestBinatedLifecycleRevisionByDocument,
  fetchBinatedArtworkProofMapping,
  patchBinatedLifecycleStatusItemDecoration,
} from "../orders";
import {
  addCoreNetworkMessage,
  assignCoreNetworkWorkRequest,
  createCoreNetworkSubmission,
  createCoreNetworkWorkRequest,
  getCoreNetworkWorkRequest,
  listCoreNetworkEvents,
  listCoreNetworkMatches,
  listCoreNetworkMessages,
  listCoreNetworkOffers,
  reviewCoreNetworkSubmission,
} from "../coreNetwork";
import {
  cancelWorkLifecycleRequest,
  createWorkLifecycleRequest,
  getWorkLifecycleRequest,
  refreshWorkLifecycleRequest,
  requestWorkLifecycleRevision,
} from "./index";

export const NORMALIZED_LIFECYCLE_STATUSES = new Set([
  "submitted",
  "matched",
  "offered",
  "assigned",
  "accepted",
  "processing",
  "awaiting_review",
  "revision_requested",
  "action_required",
  "ready",
  "completed",
  "failed_or_cancelled",
  "cancelled",
]);

const STATUS_ALIASES = {
  pending: "offered",
  sent: "offered",
  viewed: "offered",
  queued: "submitted",
  in_progress: "processing",
  proof_sent_for_approval: "awaiting_review",
  approved: "ready",
  rejected: "failed_or_cancelled",
  failed: "failed_or_cancelled",
};

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function normalizeString(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

function inferIsImage(file = {}) {
  if (file?.isImage === true) return true;
  const source = String(
    file?.previewUrl || file?.downloadUrl || file?.url || file?.fileName || ""
  ).toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/.test(source);
}

function buildFileName(file = {}) {
  const explicit = normalizeString(file?.fileName || file?.name || file?.title);
  if (explicit) return explicit;
  const url = normalizeString(file?.downloadUrl || file?.previewUrl || file?.url || file?.path);
  if (!url) return null;

  try {
    const base = url.split("?")[0].split("/").pop();
    return base || null;
  } catch (_error) {
    return null;
  }
}

export function normalizeLifecycleStatus(status, fallback = "submitted") {
  const normalized = String(status || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return fallback;
  if (NORMALIZED_LIFECYCLE_STATUSES.has(normalized)) return normalized;
  if (STATUS_ALIASES[normalized]) return STATUS_ALIASES[normalized];
  return fallback;
}

export function normalizeLifecycleFile(file = {}, options = {}) {
  if (!file || typeof file !== "object" || Array.isArray(file)) {
    return {
      fileId: null,
      fileName: null,
      downloadUrl: normalizeString(file),
      previewUrl: null,
      isImage: false,
      placementCode: null,
      placementName: null,
      productSku: null,
      productName: null,
      normalizedStatus: options.normalizedStatus || null,
      sourceProvider: options.sourceProvider || null,
    };
  }

  const downloadUrl =
    normalizeString(file.downloadUrl) ||
    normalizeString(file.url) ||
    normalizeString(file.path) ||
    normalizeString(file.internalFilePath);
  const previewUrl = normalizeString(file.previewUrl) || (inferIsImage(file) ? downloadUrl : null);
  const normalizedStatus = normalizeLifecycleStatus(
    file.normalizedStatus || options.normalizedStatus,
    null
  );

  return {
    ...file,
    fileId: normalizeString(file.fileId || file.id) || null,
    fileName: buildFileName(file),
    downloadUrl,
    previewUrl,
    isImage: inferIsImage(file),
    placementCode: normalizeString(file.placementCode) || null,
    placementName: normalizeString(file.placementName) || null,
    productSku: normalizeString(file.productSku) || null,
    productName: normalizeString(file.productName) || null,
    normalizedStatus,
    sourceProvider:
      normalizeString(file.sourceProvider || file.provider || options.sourceProvider) || null,
  };
}

export function normalizeLifecycleSnapshot(snapshot = {}, options = {}) {
  const sourceProvider =
    normalizeString(snapshot.provider) ||
    normalizeString(snapshot.sourceProvider) ||
    normalizeString(options.sourceProvider) ||
    null;
  const normalizedStatus = normalizeLifecycleStatus(
    snapshot.normalizedStatus || snapshot.status || snapshot.rawStatus,
    "submitted"
  );

  const normalizedFiles = toArray(snapshot.files).map((file) =>
    normalizeLifecycleFile(file, {
      normalizedStatus,
      sourceProvider,
    })
  );
  const normalizedResultFiles = toArray(snapshot.resultFiles).map((file) =>
    normalizeLifecycleFile(file, {
      normalizedStatus,
      sourceProvider,
    })
  );

  return {
    ...snapshot,
    provider: sourceProvider,
    normalizedStatus,
    files: normalizedFiles,
    resultFiles: normalizedResultFiles,
    revisionHistory: Array.isArray(snapshot.revisionHistory)
      ? snapshot.revisionHistory
      : Array.isArray(snapshot.revisionHistoryJson)
        ? snapshot.revisionHistoryJson
        : Array.isArray(snapshot.history)
          ? snapshot.history
          : [],
    matches: toArray(snapshot.matches),
    offers: toArray(snapshot.offers),
    assignments: toArray(snapshot.assignments),
    submissions: toArray(snapshot.submissions),
    messages: toArray(snapshot.messages),
  };
}

function extractLifecycleSnapshot(response = {}, options = {}) {
  const snapshot =
    response?.request ||
    response?.lifecycle ||
    response?.workRequest ||
    response ||
    null;
  return snapshot ? normalizeLifecycleSnapshot(snapshot, options) : null;
}

function getRequestScopedId(context = {}) {
  return context?.lifecycleRequestId || context?.sourceResourceId || null;
}

function buildCoreNetworkLifecycleSnapshot(workRequest, payload = {}) {
  if (!workRequest) return null;

  return normalizeLifecycleSnapshot(
    {
      requestId: workRequest.id,
      provider: normalizeString(workRequest.fulfillmentProvider) || "core-network",
      requestType: workRequest.requestType,
      sourceModule: workRequest.sourceModule,
      networkType: workRequest.networkType,
      status: workRequest.status,
      normalizedStatus: workRequest.normalizedStatus || workRequest.status || workRequest.rawStatus,
      rawStatus: workRequest.rawStatus,
      files: toArray(workRequest.filesJson),
      resultFiles: toArray(workRequest.resultFilesJson),
      revisionCount: Number(workRequest.revisionCount || 0),
      revisionHistory: Array.isArray(workRequest.revisionHistoryJson)
        ? workRequest.revisionHistoryJson
        : [],
      matches: payload.matches || [],
      offers: payload.offers || [],
      assignments: workRequest.assignments || [],
      submissions: workRequest.submissions || [],
      messages: payload.messages || [],
      events: payload.events || workRequest.events || [],
      submittedAt: workRequest.submittedAt,
      lastActivityAt: workRequest.lastActivityAt,
      completedAt: workRequest.completedAt,
      summary: {
        orderId: workRequest.orderId || null,
        poId: workRequest.poId || null,
        productionJobId: workRequest.productionJobId || null,
        documentType: workRequest.documentType || null,
        documentId: workRequest.documentId || null,
      },
    },
    {
      sourceProvider: normalizeString(workRequest.fulfillmentProvider) || "core-network",
    }
  );
}

export function createBinatedLifecycleAdapter() {
  return {
    provider: "binated",
    load: ({ sourceResourceId, contextPayload }) =>
      getBinatedLifecycleByDocument(sourceResourceId, contextPayload),
    refresh: ({ sourceResourceId, contextPayload }) =>
      refreshBinatedLifecycleByDocument(sourceResourceId, contextPayload),
    requestRevision: ({ sourceResourceId, contextPayload }, payload) =>
      requestBinatedLifecycleRevisionByDocument(sourceResourceId, {
        ...contextPayload,
        ...payload,
      }),
    loadMappingContext: ({ sourceResourceId, contextPayload }, payload) =>
      fetchBinatedArtworkProofMapping(sourceResourceId, {
        ...contextPayload,
        ...payload,
      }),
    patchStatusItemDecoration: ({ lifecycleRequestId }, { requestId, placementUuid, ...payload }) =>
      patchBinatedLifecycleStatusItemDecoration(
        requestId || lifecycleRequestId,
        placementUuid,
        payload
      ),
    normalizeResponse: (response) =>
      extractLifecycleSnapshot(response, { sourceProvider: "binated" }),
  };
}

export function createCoreNetworkLifecycleAdapter() {
  return {
    provider: "core-network",
    create: (_context, payload = {}) => createCoreNetworkWorkRequest(payload),
    load: async (context) => {
      const resourceId = getRequestScopedId(context);
      const payload = context?.contextPayload || {};
      const [workRequestResponse, messagesResponse, eventsResponse, matchesResponse, offersResponse] =
        await Promise.all([
          getCoreNetworkWorkRequest(resourceId, payload),
          listCoreNetworkMessages(resourceId, {
            ...payload,
            includeInternal: payload.includeInternal === true ? "true" : "false",
          }),
          listCoreNetworkEvents(resourceId, payload),
          listCoreNetworkMatches(resourceId, payload),
          listCoreNetworkOffers(resourceId, payload),
        ]);

      return {
        request: buildCoreNetworkLifecycleSnapshot(workRequestResponse?.workRequest, {
          messages: messagesResponse?.messages || [],
          events: eventsResponse?.events || [],
          matches: matchesResponse?.matches || [],
          offers: offersResponse?.offers || [],
        }),
      };
    },
    refresh(context) {
      return this.load(context);
    },
    requestRevision: (context, payload = {}) =>
      requestWorkLifecycleRevision("core-network", getRequestScopedId(context), payload),
    cancel: (context, payload = {}) =>
      cancelWorkLifecycleRequest("core-network", getRequestScopedId(context), payload),
    assignMember: (context, payload = {}) =>
      assignCoreNetworkWorkRequest(getRequestScopedId(context), payload),
    addMessage: (context, payload = {}) =>
      addCoreNetworkMessage(getRequestScopedId(context), payload),
    createSubmission: (context, payload = {}) =>
      createCoreNetworkSubmission(getRequestScopedId(context), payload),
    reviewSubmission: (submissionId, payload = {}) => reviewCoreNetworkSubmission(submissionId, payload),
    normalizeResponse: (response) =>
      extractLifecycleSnapshot(response, { sourceProvider: "core-network" }),
  };
}

export function createInternalLifecycleAdapter() {
  return {
    provider: "internal",
    create: (_context, payload = {}) => createWorkLifecycleRequest("internal", payload),
    load: (context) =>
      getWorkLifecycleRequest("internal", getRequestScopedId(context), context?.contextPayload || {}),
    refresh: (context) =>
      refreshWorkLifecycleRequest("internal", getRequestScopedId(context), context?.contextPayload || {}),
    requestRevision: (context, payload = {}) =>
      requestWorkLifecycleRevision("internal", getRequestScopedId(context), {
        ...(context?.contextPayload || {}),
        ...payload,
      }),
    cancel: (context, payload = {}) =>
      cancelWorkLifecycleRequest("internal", getRequestScopedId(context), payload),
    normalizeResponse: (response) => extractLifecycleSnapshot(response, { sourceProvider: "internal" }),
  };
}
